import moment from 'moment-timezone'
import { PropsWithChildren, FC, ReactElement } from 'react'
import { useSelector } from 'react-redux'
import { selectFlags as selectOrderDetailsFlags } from '@Orders/OrderDetailsPage/selectors'
import { salesOrder_salesOrder_shipments_items as SalesOrderShipmentsItemType } from '@Orders/OrderDetailsPage//Queries/__generated__/salesOrder'
import { shipmentIdsMapType } from '@Orders/OrderDetailsPage/logic'
import { salesOrderLines_salesOrderLines_items_shipmentAndInspection as ShipmentAndInspectionType } from '@Orders/OrderDetailsPage/Queries/__generated__/salesOrderLines'
import {
    PARTIAL_SHIPPING_DISPLAY,
    PARTIAL_SHIPPING_STATUS,
    SHIPPED_DISPLAY,
    SHIPPED_STATUS,
} from '@Orders/constants'
import { FileNameProcessInterface } from '@Orders/OrderDetailsPage/Status/types'
import { ExclamationCircleOutlined } from '@ant-design/icons'
import { Tooltip } from 'antd'

import Text from '~/core/Text'
import Box from '~/core/Box'
import {
    TOOLING_CHANGE_PROCESSES_ID,
    TOOLING_PROCESSES_IDS,
    TOOLING_PROCESSES_SAMPLES_ID,
} from '~/lib/partsConstants'
import getColor from '~/lib/getColor'

import { ToggleModalForPartViewerFuncType } from './logic'
import ShipmentsLineItems from './ShipmentsLineItems'

type PropType = {
    deliveredQuantity: number | null
    isImOrder: boolean
    orderedQuantity: number
    processes: FileNameProcessInterface[]
    processName: string | null
    shippedBy: string
    shipments: (ShipmentAndInspectionType | null)[]
    shipmentsData?: SalesOrderShipmentsItemType[]
    shipmentIdsMap: shipmentIdsMapType
    status: string
    toggleModalForPartViewer: ToggleModalForPartViewerFuncType
}

const ShippedText: ReactElement = (
    <Text color="success" style={{ whiteSpace: 'nowrap', marginRight: '4px' }}>
        {SHIPPED_DISPLAY}
    </Text>
)

const RenderShipping: FC<PropsWithChildren<PropType>> = (props: PropType) => {
    const {
        deliveredQuantity,
        isImOrder,
        orderedQuantity,
        processes,
        shippedBy,
        shipments,
        shipmentsData,
        shipmentIdsMap,
        status,
        toggleModalForPartViewer,
    } = props
    const { isTransparencyImprovementEnabled } = useSelector(
        selectOrderDetailsFlags,
    )

    const isTooling = processes.some(
        ({ platformProcessId }) =>
            platformProcessId &&
            (TOOLING_CHANGE_PROCESSES_ID?.indexOf(platformProcessId) >= 0 ||
                TOOLING_PROCESSES_IDS?.indexOf(platformProcessId) >= 0),
    )

    const isShippingStatus =
        status === SHIPPED_STATUS || status === PARTIAL_SHIPPING_STATUS
    const isSample = processes.some(
        ({ platformProcessId }) =>
            platformProcessId &&
            TOOLING_PROCESSES_SAMPLES_ID?.indexOf(platformProcessId) >= 0,
    )

    if (status === PARTIAL_SHIPPING_STATUS && !isTooling && !isImOrder) {
        const shipmentStatus: ReactElement = (
            <Box whiteSpace="pre">
                <Text color="blackAlpha45">
                    {deliveredQuantity} of {orderedQuantity}{' '}
                </Text>
                <Text color="blackAlpha45">{SHIPPED_DISPLAY}</Text>
            </Box>
        )

        return isTransparencyImprovementEnabled ? (
            <ShipmentsLineItems
                {...{
                    shipmentStatus,
                    shipments,
                    shipmentsData,
                    shipmentIdsMap,
                    toggleModalForPartViewer,
                }}
            />
        ) : (
            shipmentStatus
        )
    }

    if (status === SHIPPED_STATUS && !isTooling) {
        return isTransparencyImprovementEnabled ? (
            <ShipmentsLineItems
                {...{
                    shipmentStatus: ShippedText,
                    shipments,
                    shipmentsData,
                    shipmentIdsMap,
                    toggleModalForPartViewer,
                }}
            />
        ) : (
            ShippedText
        )
    }
    if (status === PARTIAL_SHIPPING_STATUS && !isTooling) {
        return <Text>{PARTIAL_SHIPPING_DISPLAY}</Text>
    }
    const isValidShipDate = moment() < moment(shippedBy)

    if (isValidShipDate && !isShippingStatus && isSample) {
        const shipDate = moment(shippedBy).format('dddd, MM/DD/YYYY')
        const imEstShippingDisclamer =
            'Estimated ship date based on typical final DFM approval schedule. Estimated ship date will be updated upon final DFM approval'

        return (
            <>
                <Text text="Est. Ship Date" />
                {isImOrder && (
                    <Tooltip
                        title={imEstShippingDisclamer}
                        placement="topLeft"
                        arrowPointAtCenter
                        overlayStyle={{ maxWidth: '298px' }}
                    >
                        <ExclamationCircleOutlined
                            style={{
                                marginLeft: 5,
                                color: getColor('info6'),
                            }}
                        />
                    </Tooltip>
                )}
                <div>{shipDate}</div>
            </>
        )
    }

    return <>-</>
}

export default RenderShipping
